import { useEffect, useRef, useState } from 'react';
import type { ImportResult } from '@schwarz/contract';
import { CSV_FIELDS } from '@schwarz/contract';

import { Modal } from '../../ui/Modal.js';
import { IconAlert, IconCheck, IconDownload, IconUpload } from '../../ui/icons.js';
import modal from '../../ui/Modal.module.css';
import styles from './ImportDialog.module.css';

/** Column names the file is read against, shown so a first attempt can succeed. */
const COLUMN_HINT = 'name, company, phone, email, greeting';

/**
 * Import contacts from a CSV (S05).
 *
 * Two states in one dialog: pick a file, then read what the import did. The
 * result is shown here rather than in a toast because a toast cannot hold a list
 * of rejected rows, and "112 imported, 3 rejected" with no way to see which three
 * is the same silent data loss the old tool had — it just admits to it.
 *
 * The file is not parsed here. The browser reads bytes and posts them; the server
 * decides what the columns mean, which is what keeps one definition of the format
 * rather than two that drift.
 */
export function ImportDialog({
  open,
  busy,
  result,
  error,
  onCancel,
  onImport,
  onDone,
}: {
  open: boolean;
  busy: boolean;
  result: ImportResult | null;
  error: string | null;
  onCancel: () => void;
  onImport: (file: File) => void;
  onDone: () => void;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // Reopening should not still be holding the last file, and should not show
  // the previous run's result underneath a fresh pick.
  useEffect(() => {
    if (open) {
      setFile(null);
      setOver(false);
      if (input.current) input.current.value = '';
    }
  }, [open]);

  const showing = result !== null;

  return (
    <Modal
      open={open}
      title={showing ? 'Import finished' : 'Import contacts from CSV'}
      subtitle={
        showing ? undefined : 'Reads a header row if there is one. A greeting column is optional.'
      }
      onClose={showing ? onDone : onCancel}
      labelledBy="import-title"
      footer={
        showing ? (
          <button type="button" className={modal.primary} onClick={onDone}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className={modal.ghost} onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className={modal.primary}
              disabled={busy || file === null}
              onClick={() => file && onImport(file)}
            >
              <IconUpload />
              {busy ? 'Importing…' : 'Import contacts'}
            </button>
          </>
        )
      }
    >
      {showing ? (
        <Report result={result} />
      ) : (
        <>
          <label
            className={`${styles.drop} ${over || file ? styles.dropHas : ''}`}
            htmlFor="import-file"
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setOver(false);
              const dropped = event.dataTransfer.files[0];
              if (dropped) setFile(dropped);
            }}
          >
            <IconUpload strokeWidth={1.8} />
            <b>{file ? file.name : 'Choose a CSV file'}</b>
            <small>
              {file ? readableSize(file.size) : `or drag it here · columns: ${COLUMN_HINT}`}
            </small>
          </label>
          <input
            ref={input}
            id="import-file"
            type="file"
            accept=".csv,.tsv,.txt,text/csv"
            className={styles.file}
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />

          <p className={styles.note}>
            Existing contacts are matched on phone number and updated rather than duplicated.
          </p>

          {error && (
            <p className={modal.error}>
              <IconAlert width="14" height="14" /> {error}
            </p>
          )}
        </>
      )}
    </Modal>
  );
}

/** What the import did, in the order an operator wants to know it. */
function Report({ result }: { result: ImportResult }): JSX.Element {
  const changed = result.created + result.updated;

  return (
    <div className={styles.report}>
      <div className={styles.tally}>
        <Count value={result.created} label="added" tone="good" />
        <Count value={result.updated} label="updated" tone="plain" />
        <Count value={result.rejected.length} label="rejected" tone="bad" />
      </div>

      <p className={styles.mapping}>
        {result.mapping.source === 'header'
          ? 'Read from the header row: '
          : 'No header row found, so columns were read by position: '}
        <b>{describeColumns(result.mapping.columns)}</b>
      </p>

      {result.warnings.length > 0 && (
        <ul className={styles.warnings}>
          {result.warnings.map((warning, index) => (
            <li key={`${warning.row}-${index}`}>
              <IconAlert width="13" height="13" />
              <span>
                {warning.row > 1 && <b>Row {warning.row}: </b>}
                {warning.message}
              </span>
            </li>
          ))}
        </ul>
      )}

      {result.rejected.length > 0 ? (
        <>
          <div className={styles.rejectHead}>
            <h4>Not imported</h4>
            <button type="button" className={styles.download} onClick={() => download(result)}>
              <IconDownload width="13" height="13" />
              Download report
            </button>
          </div>
          <div className={styles.rejects}>
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Contact</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {result.rejected.map((row) => (
                  <tr key={row.row}>
                    <td className={styles.rowNo}>{row.row}</td>
                    <td>{row.name ?? row.phone ?? '—'}</td>
                    <td className={styles.reason}>{row.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className={styles.clean}>
          <IconCheck width="14" height="14" />
          Every row in the file was imported.
        </p>
      )}

      {changed === 0 && result.rejected.length > 0 && (
        <p className={styles.nothing}>
          Nothing was written. If every row failed the same way, the columns were probably read
          differently than you meant — check the mapping above.
        </p>
      )}
    </div>
  );
}

function Count({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: 'good' | 'bad' | 'plain';
}): JSX.Element {
  return (
    <div className={`${styles.count} ${styles[tone]}`}>
      <b>{value}</b>
      <small>{label}</small>
    </div>
  );
}

/** "name → column 1, phone → column 3, greeting not present" — in file terms. */
function describeColumns(columns: ImportResult['mapping']['columns']): string {
  const present = CSV_FIELDS.filter((field) => columns[field] !== null).map(
    (field) => `${label(field)} → column ${(columns[field] ?? 0) + 1}`,
  );
  const absent = CSV_FIELDS.filter((field) => columns[field] === null).map(label);

  if (absent.length === 0) return present.join(', ');
  return `${present.join(', ')} · not in the file: ${absent.join(', ')}`;
}

function label(field: (typeof CSV_FIELDS)[number]): string {
  return field === 'salutation' ? 'greeting' : field;
}

/**
 * The rejected rows as a CSV, built in the browser from the response.
 *
 * No server round trip: the report is already in hand, and a stored file would
 * need a lifetime, a cleanup job and an access check to earn its keep.
 */
function download(result: ImportResult): void {
  const rows = [
    ['row', 'name', 'phone', 'reason'],
    ...result.rejected.map((row) => [String(row.row), row.name ?? '', row.phone ?? '', row.reason]),
  ];
  const csv = rows.map((row) => row.map(quote).join(',')).join('\r\n');

  // The BOM is what makes Excel open a UTF-8 file as UTF-8 rather than as the
  // local codepage, which is the difference between "Петър" and "ÐŸÐµÑ‚ÑŠÑ€".
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${result.filename.replace(/\.[^.]+$/, '')}-rejected.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/** RFC 4180 quoting, so a reason containing a comma survives the round trip. */
function quote(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
